import { useEffect, useRef } from "react";

const W = 1000, H = 400, PAD_R = 104, TOP = 18, BOTTOM = 368;

const LevelLine = ({ y, x1, label, value, cls }) => <g className={`level ${cls}`}>
  <line x1={x1} x2={W - PAD_R} y1={y} y2={y} />
  <rect x={W - PAD_R + 2} y={y - 9} width={PAD_R - 4} height={18} rx="3" />
  <text className="tag" x={W - PAD_R + 7} y={y + 4}>{label} {value.toFixed(1)}</text>
</g>;

const Marker = ({ s, x, y, candle }) => {
  const buy = s.type === "BUY";
  const tipY = buy ? y(candle.low) + 8 : y(candle.high) - 8;
  const arrow = buy ? `M${x} ${tipY} l-7 12 h14 z` : `M${x} ${tipY} l-7 -12 h14 z`;
  return <g className={`marker ${buy ? "buy" : "sell"}`} data-testid={`chart-marker-${s.type.toLowerCase()}-${s.index}`}>
    <line x1={x} x2={x} y1={y(s.price)} y2={tipY} className="marker-stem" />
    <circle cx={x} cy={y(s.price)} r="4.5" className="marker-dot" />
    <path d={arrow} />
    <text x={x} y={buy ? tipY + 26 : tipY - 18} textAnchor="middle">{s.type} {s.price.toFixed(1)}</text>
  </g>;
};

export const CandleChart = ({ candles, signals, levels, overlays, testId, live }) => {
  const box = useRef(null);
  useEffect(() => { if (box.current) box.current.scrollLeft = box.current.scrollWidth; }, [candles.length]);
  const prices = candles.flatMap(c => [c.high, c.low]).concat(levels ? [levels.target, levels.stop_loss] : []);
  const max = Math.max(...prices), min = Math.min(...prices), pad = (max - min) * 0.1;
  const y = p => TOP + ((max + pad - p) / (max - min + pad * 2)) * (BOTTOM - TOP);
  const step = (W - PAD_R - 10) / candles.length, x = i => 8 + i * step + step / 2;
  const bodyW = Math.max(2, step * 0.62);
  const path = vals => vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const ticks = [0, 1, 2, 3, 4].map(i => min - pad + ((max - min + pad * 2) * i) / 4);
  const last = candles[candles.length - 1], lastUp = last.close >= last.open;
  return <div className="cc" ref={box} data-testid={testId}>
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Live candlestick chart with signals">
      {ticks.map(t => <g key={t}><line x1="0" x2={W - PAD_R} y1={y(t)} y2={y(t)} className="grid-line" /><text x={W - PAD_R + 7} y={y(t) + 3} className="axis">{t.toFixed(1)}</text></g>)}
      <path d={path(overlays.vwap)} className="vwap-line" />
      <path d={path(overlays.ema_fast)} className="ema-line" />
      {candles.map((c, i) => { const up = c.close >= c.open; return <g key={i} className={`${up ? "up" : "down"} ${i === candles.length - 1 && live ? "live-candle" : ""}`}>
        <line x1={x(i)} x2={x(i)} y1={y(c.high)} y2={y(c.low)} className="wick" />
        <rect x={x(i) - bodyW / 2} y={Math.min(y(c.open), y(c.close))} width={bodyW} height={Math.max(1.5, Math.abs(y(c.open) - y(c.close)))} rx="1" />
      </g>; })}
      {levels && <>
        <LevelLine y={y(levels.target)} x1={x(levels.from_index)} label="TGT" value={levels.target} cls="target" />
        <LevelLine y={y(levels.entry)} x1={x(levels.from_index)} label="ENTRY" value={levels.entry} cls="entry" />
        <LevelLine y={y(levels.stop_loss)} x1={x(levels.from_index)} label="SL" value={levels.stop_loss} cls="stop" />
      </>}
      {signals.map(s => <Marker key={s.id} s={s} x={x(s.index)} y={y} candle={candles[s.index]} />)}
      <g className={`live-price ${lastUp ? "up" : "down"}`} data-testid="chart-live-price">
        <line x1="0" x2={W - PAD_R} y1={y(last.close)} y2={y(last.close)} />
        <rect x={W - PAD_R + 2} y={y(last.close) - 10} width={PAD_R - 4} height={20} rx="3" />
        <text x={W - PAD_R + 7} y={y(last.close) + 4}>{last.close.toFixed(2)}</text>
      </g>
      {candles.map((c, i) => i % 12 === 0 && <text key={c.time} x={x(i)} y={H - 8} className="axis" textAnchor="middle">{c.time}</text>)}
    </svg>
  </div>;
};
